import clsx from 'clsx'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import arrowIcon from 'assets/svg/arrowDown.svg'
import editIcon from 'assets/svg/edit.svg'
import safeIcon from 'assets/svg/safe.svg'
import hStoreLogo from 'assets/svg/shops/h-store.svg'
import iportLogo from 'assets/svg/shops/iport.svg'
import samsungLogo from 'assets/svg/shops/samsung.svg'
import thumbDown from 'assets/svg/thumbDown.svg'
import thumbUp from 'assets/svg/thumbUp.svg'

import { Icon } from '@/shared/ui/icon'

import { formatReviewDate } from '../../lib/format-review-date'
import { ProductReview, ProductReviewShop, ProductReviewVote } from '../../model/types'
import { ReviewMediaPreview } from '../media/ReviewMediaPreview'
import { Stars } from '../stars/Stars'

import styles from './ReviewCard.module.scss'

const CURRENT_SHOP: ProductReviewShop | '' = 'iport'

const SHOP_LOGOS: Partial<
	Record<ProductReviewShop, { icon: typeof iportLogo; width: number; height: number; label: string }>
> = {
	'h-store': { icon: hStoreLogo, width: 59, height: 20, label: 'H-Store' },
	iport: { icon: iportLogo, width: 51, height: 16, label: 'iPort' },
	samsung: { icon: samsungLogo, width: 75, height: 12, label: 'Samsung' }
}

const getPartnerUrl = (review: ProductReview) => {
	if (!review.shop || !review.shopUrl) return undefined
	return review.shopUrl
}

export const ReviewCard = ({
	review,
	onVote,
	onOpenMedia,
	isVoteDisabled = false,
	onEdit,
	productHref,
	hideMedia = false,
	disableTextCollapse = false,
	activeMediaSrc,
	isHighlighted = false,
	isVoting: _isVoting,
	className
}: {
	review: ProductReview
	onVote: (vote: ProductReviewVote) => void
	onOpenMedia?: (photoIndex: number, src: string) => void
	onEdit?: (review: ProductReview) => void
	isVoteDisabled?: boolean
	productHref?: string
	hideMedia?: boolean
	disableTextCollapse?: boolean
	activeMediaSrc?: string
	isHighlighted?: boolean
	isVoting?: boolean
	className?: string
}) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [isLong, setIsLong] = useState(false)
	const reviewTextRef = useRef<HTMLParagraphElement>(null)
	const shopLogo = review.shop ? SHOP_LOGOS[review.shop] : null
	const partnerUrl = getPartnerUrl(review)
	const shouldShowPartnerLink = shopLogo && partnerUrl && (!CURRENT_SHOP || review.shop !== CURRENT_SHOP)

	useEffect(() => {
		const reviewText = reviewTextRef.current
		if (!reviewText) return

		const updateOverflow = () => {
			const lineHeight = Number.parseFloat(window.getComputedStyle(reviewText).lineHeight)
			setIsLong(reviewText.scrollHeight > lineHeight * 4 + 1)
		}

		updateOverflow()
		const resizeObserver = new ResizeObserver(updateOverflow)
		resizeObserver.observe(reviewText)
		return () => resizeObserver.disconnect()
	}, [review.comment])

	return (
		<article
			className={clsx(
				styles.reviewCard,
				isHighlighted && styles.reviewHighlighted,
				!review.active && styles.reviewPending,
				className
			)}
			data-review-id={review.id}>
			{productHref && (
				<Link
					className={styles.productLink}
					href={productHref}
					aria-label={'Open product reviewed by ' + review.author}
				/>
			)}
			<div className={styles.reviewHeader}>
				<div>
					<div className={styles.authorRow}>
						<strong>{review.author}</strong>
						{!review.active && <span>На модерации</span>}
					</div>
				</div>
				<div className={styles.headerActions}>
					<div className={styles.rating}>
						<Stars rating={review.rating} />
					</div>
					{onEdit && (
						<button
							type="button"
							className={styles.editButton}
							onClick={() => onEdit(review)}
							aria-label="Редактировать отзыв">
							<Icon icon={editIcon} width={24} height={24} title="" />
						</button>
					)}
				</div>
			</div>

			{review.verified && (
				<span className={styles.verifiedPurchase}>
					<Icon icon={safeIcon} width={16} height={16} title="" />
					Покупка подтверждена
				</span>
			)}

			<p
				ref={reviewTextRef}
				className={clsx(styles.reviewText, !disableTextCollapse && !isExpanded && styles.reviewTextCollapsed)}>
				{review.comment}
			</p>

			{isLong && !disableTextCollapse && (
				<button type="button" className={styles.showFullButton} onClick={() => setIsExpanded(value => !value)}>
					{isExpanded ? 'Свернуть' : 'Показать полностью'}
					<Icon
						className={clsx(styles.showFullArrow, isExpanded && styles.showFullArrowExpanded)}
						icon={arrowIcon}
						width={16}
						height={16}
						title=""
					/>
				</button>
			)}

			{!hideMedia && review.photos.length > 0 && (
				<div className={styles.reviewPhotos}>
					{review.photos.map((photo, index) => (
						<button
							type="button"
							className={clsx(
								styles.reviewMediaButton,
								photo === activeMediaSrc && styles.reviewMediaButtonActive,
								!onOpenMedia && styles.reviewMediaButtonStatic
							)}
							key={`${photo}-${index}`}
							onClick={() => onOpenMedia?.(index, photo)}>
							<ReviewMediaPreview
								className={styles.reviewMediaPreview}
								src={photo}
								alt={`Материал к отзыву ${index + 1}`}
							/>
						</button>
					))}
				</div>
			)}

			<div className={styles.reviewFooter}>
				<div className={styles.reviewActionsRow}>
					<time dateTime={review.createdAt}>{formatReviewDate(review.createdAt)}</time>
					<div className={styles.votes} aria-label="Оценить отзыв">
						<button
							type="button"
							disabled={isVoteDisabled}
							className={review.userVote === 'like' ? styles.voteActive : undefined}
							onClick={() => onVote('like')}
							aria-label="Отзыв полезен">
							<Icon
								className={clsx(styles.voteIcon, styles.voteIconLike)}
								icon={thumbUp}
								width={20}
								height={20}
								title=""
							/>
							<span>{review.likes}</span>
						</button>
						<button
							type="button"
							disabled={isVoteDisabled}
							className={review.userVote === 'dislike' ? styles.voteActive : undefined}
							onClick={() => onVote('dislike')}
							aria-label="Отзыв не полезен">
							<Icon
								className={clsx(styles.voteIcon, styles.voteIconDislike)}
								icon={thumbDown}
								width={20}
								height={20}
								title=""
							/>
							<span>{review.dislikes}</span>
						</button>
					</div>
				</div>
				{shouldShowPartnerLink && (
					<a
						className={styles.partnerLink}
						href={partnerUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={`Перейти на сайт ${shopLogo.label}`}>
						<Icon
							className={styles.shopLogo}
							icon={shopLogo.icon}
							width={shopLogo.width}
							height={shopLogo.height}
							title=""
						/>
						<span className={styles.partnerDot} aria-hidden="true" />
						<span className={styles.partnerText}>отзыв с сайта партнера</span>
					</a>
				)}
			</div>
		</article>
	)
}
